<template>
  <div class="agent-login-container">
    <div class="login-card">
      <!-- Logo/Header -->
      <div class="login-header">
        <div class="logo-circle">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
        </div>
        <h1>Agent Login</h1>
        <p>Sign in to access your dashboard</p>
      </div>

      <!-- Login Form -->
      <form @submit.prevent="handleLogin" class="login-form">
        <!-- Agent Code Input -->
        <div class="form-group">
          <label for="agent_code">Agent Code</label>
          <div class="input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="input-icon">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
            </svg>
            <input
              type="text"
              id="agent_code"
              v-model="agentCode"
              placeholder="Enter your agent code"
              required
              :disabled="isLoading"
              autocomplete="username"
            />
          </div>
        </div>

        <!-- Password Input -->
        <div class="form-group">
          <label for="password">Password</label>
          <div class="input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="input-icon">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
            </svg>
            <input
              :type="showPassword ? 'text' : 'password'"
              id="password"
              v-model="password"
              placeholder="Enter your password"
              required
              :disabled="isLoading"
              autocomplete="current-password"
            />
            <button
              type="button"
              class="toggle-password"
              @click="showPassword = !showPassword"
              :disabled="isLoading"
            >
              <svg v-if="!showPassword" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
              </svg>
              <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Remember Me -->
        <div class="remember-me">
          <label class="remember-label">
            <input type="checkbox" v-model="rememberMe" />
            <span class="checkmark"></span>
            Remember me
          </label>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          {{ errorMessage }}
        </div>

        <!-- Turnstile -->
        <TurnstileWidget
          ref="turnstile"
          :site-key="turnstileSiteKey"
          v-model:token="turnstileToken"
        />

        <!-- Login Button -->
        <button type="submit" class="login-button" :disabled="isLoading">
          <span v-if="!isLoading">Sign In</span>
          <span v-else class="loading-spinner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" opacity="0.25"/>
              <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
            </svg>
            Signing in...
          </span>
        </button>
      </form>

      <!-- Help Text -->
      <div class="help-text">
        <p>Don't have your credentials?</p>
        <p>Contact your administrator to get your agent code and password.</p>
      </div>

      <!-- Switch to User Login -->
      <div class="switch-login">
        <router-link to="/login" class="switch-login-link">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
          </svg>
          Switch to User Login
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import TurnstileWidget from '@/components/TurnstileWidget.vue'

export default {
  name: 'AgentLoginView',
  components: { TurnstileWidget },
  data () {
    return {
      agentCode: '',
      password: '',
      showPassword: false,
      isLoading: false,
      errorMessage: '',
      rememberMe: true,
      // Turnstile
      turnstileToken: '',
      turnstileSiteKey: process.env.VUE_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAC_1sD05pRRxvRCk'
    }
  },
  mounted () {
    // Check if already logged in
    const agentToken = localStorage.getItem('agent_token')
    if (agentToken) {
      this.$router.push('/agent-dashboard')
      return
    }

    // Restore saved credentials
    const saved = localStorage.getItem('agent_remember')
    if (saved) {
      try {
        const { agentCode } = JSON.parse(saved)
        this.agentCode = agentCode || ''
        this.rememberMe = true
      } catch (_) {}
    }
  },
  methods: {
    /**
     * Generate a simple browser fingerprint
     * This creates a hash based on browser characteristics
     */
    generateDeviceFingerprint () {
      const nav = window.navigator
      const screen = window.screen

      const fingerprint = [
        nav.userAgent,
        nav.language,
        screen.colorDepth,
        screen.width + 'x' + screen.height,
        new Date().getTimezoneOffset(),
        !!window.sessionStorage,
        !!window.localStorage
      ].join('|')

      // Simple hash function
      let hash = 0
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash
      }

      return Math.abs(hash).toString(36)
    },

    /**
     * Get a human-readable device name
     */
    getDeviceName () {
      const nav = window.navigator
      const userAgent = nav.userAgent

      // Detect browser
      let browser = 'Unknown Browser'
      if (userAgent.indexOf('Firefox') > -1) browser = 'Firefox'
      else if (userAgent.indexOf('Chrome') > -1) browser = 'Chrome'
      else if (userAgent.indexOf('Safari') > -1) browser = 'Safari'
      else if (userAgent.indexOf('Edge') > -1) browser = 'Edge'
      else if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident') > -1) browser = 'Internet Explorer'

      // Detect OS
      let os = 'Unknown OS'
      if (userAgent.indexOf('Win') > -1) os = 'Windows'
      else if (userAgent.indexOf('Mac') > -1) os = 'macOS'
      else if (userAgent.indexOf('Linux') > -1) os = 'Linux'
      else if (userAgent.indexOf('Android') > -1) os = 'Android'
      else if (userAgent.indexOf('iOS') > -1) os = 'iOS'

      return `${browser} on ${os}`
    },

    async handleLogin () {
      // Clear previous error
      this.errorMessage = ''

      // Validate inputs
      if (!this.agentCode.trim()) {
        this.errorMessage = 'Please enter your agent code'
        return
      }

      if (!this.password) {
        this.errorMessage = 'Please enter your password'
        return
      }

      if (!this.turnstileToken) {
        this.errorMessage = 'Please complete the security check.'
        return
      }

      this.isLoading = true

      try {
        // Generate device fingerprint and device name
        const deviceFingerprint = this.generateDeviceFingerprint()
        const deviceName = this.getDeviceName()

        const response = await fetch('https://api.quali-d.com/agents/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            agent_code: this.agentCode.trim(),
            password: this.password,
            platform: 'web',
            device_fingerprint: deviceFingerprint,
            device_name: deviceName,
            cf_turnstile_response: this.turnstileToken
          })
        })

        const data = await response.json()

        if (!response.ok) {
          this.errorMessage = data.message || 'Login failed. Please check your credentials.'
          this.$refs.turnstile?.reset()
          return
        }

        if (data.success && data.agent) {
          // Store agent token - backend will authenticate agents using their own token
          localStorage.setItem('agent_token', data.agent.token)
          // Also store in auth_token for compatibility with existing services
          localStorage.setItem('auth_token', data.agent.token)

          // Store agent data in localStorage
          localStorage.setItem('agent_data', JSON.stringify({
            id: data.agent.id,
            name: data.agent.name,
            agent_code: data.agent.agent_code,
            department_id: data.agent.department_id,
            department_name: data.agent.department_name,
            agent_role_id: data.agent.agent_role_id,
            agent_role_name: data.agent.agent_role_name,
            company_name: data.agent.company_name,
            owner_id: data.agent.owner_id
          }))

          // Check if previous session was kicked
          if (data.session && data.session.previous_session_revoked) {
            // Previous session was revoked (single-session enforcement)
          }

          // Save or clear credentials based on Remember Me
          if (this.rememberMe) {
            localStorage.setItem('agent_remember', JSON.stringify({
              agentCode: this.agentCode.trim()
            }))
          } else {
            localStorage.removeItem('agent_remember')
          }

          // Redirect Marketing agents to templates, all other agents to dashboard
          if (data.agent.agent_role_name === 'Marketing' || parseInt(data.agent.agent_role_id) === 3) {
            this.$router.push('/whatsapp-templates')
          } else {
            this.$router.push('/agent-dashboard')
          }
        } else {
          this.errorMessage = 'Login failed. Please try again.'
        }
      } catch (error) {
        this.errorMessage = 'Network error. Please check your connection and try again.'
        this.$refs.turnstile?.reset()
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.agent-login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.login-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 440px;
  padding: 48px 40px;
}

.login-header {
  text-align: center;
  margin-bottom: 40px;
}

.logo-circle {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: white;
}

.login-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 8px 0;
}

.login-header p {
  font-size: 14px;
  color: #718096;
  margin: 0;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  color: #a0aec0;
  pointer-events: none;
}

.input-wrapper input {
  width: 100%;
  padding: 14px 16px 14px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
  color: #2d3748;
}

.input-wrapper input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.input-wrapper input:disabled {
  background: #f7fafc;
  cursor: not-allowed;
}

.toggle-password {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.toggle-password:hover:not(:disabled) {
  color: #667eea;
}

.toggle-password:disabled {
  cursor: not-allowed;
}

.remember-me {
  margin-top: -8px;
}

.remember-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #4a5568;
  cursor: pointer;
  user-select: none;
}

.remember-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #667eea;
  cursor: pointer;
  flex-shrink: 0;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fff5f5;
  border: 1px solid #fc8181;
  border-radius: 8px;
  color: #c53030;
  font-size: 14px;
  margin-top: -8px;
}

.error-message svg {
  flex-shrink: 0;
}

.login-button {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.loading-spinner svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.help-text {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  text-align: center;
}

.help-text p {
  font-size: 13px;
  color: #718096;
  margin: 4px 0;
}

.help-text p:first-child {
  font-weight: 600;
  color: #4a5568;
}

.switch-login {
  margin-top: 24px;
  text-align: center;
}

.switch-login-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  border: 2px solid #667eea;
  border-radius: 10px;
  color: #667eea;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
}

.switch-login-link:hover {
  background: #667eea;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.switch-login-link svg {
  transition: transform 0.2s;
}

.switch-login-link:hover svg {
  transform: translateX(4px);
}

/* Responsive */
@media (max-width: 480px) {
  .login-card {
    padding: 32px 24px;
  }

  .login-header h1 {
    font-size: 24px;
  }

  .logo-circle {
    width: 64px;
    height: 64px;
  }

  .logo-circle svg {
    width: 32px;
    height: 32px;
  }
}
</style>

<style>
/* Dark mode overrides — .dark is set on <html> by themeStore */
.dark .agent-login-container {
  background: linear-gradient(135deg, #3b4a7a 0%, #4a2d6b 100%);
}

.dark .login-card {
  background: #1e2535;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
}

.dark .login-header h1 {
  color: #e2e8f0;
}

.dark .login-header p {
  color: #94a3b8;
}

.dark .form-group label {
  color: #cbd5e1;
}

.dark .input-wrapper input {
  background: #2d3748;
  border-color: #4a5568;
  color: #e2e8f0;
}

.dark .input-wrapper input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
}

.dark .input-wrapper input:disabled {
  background: #1a202c;
}

.dark .input-wrapper input::placeholder {
  color: #718096;
}

.dark .input-icon {
  color: #718096;
}

.dark .toggle-password {
  color: #718096;
}

.dark .toggle-password:hover:not(:disabled) {
  color: #818cf8;
}

.dark .remember-label {
  color: #94a3b8;
}

.dark .error-message {
  background: #2d1515;
  border-color: #c53030;
  color: #fc8181;
}

.dark .help-text {
  border-top-color: #2d3748;
}

.dark .help-text p {
  color: #718096;
}

.dark .help-text p:first-child {
  color: #94a3b8;
}

.dark .switch-login-link {
  border-color: #818cf8;
  color: #818cf8;
}

.dark .switch-login-link:hover {
  background: #818cf8;
  color: white;
  box-shadow: 0 4px 12px rgba(129, 140, 248, 0.3);
}
</style>
